// src/pwa/pages/myBookPwa.jsx
import React, { useEffect, useState } from 'react';
import usePwaAuth from '../hooks/usePwaAuth';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import '../styles/pwa.css'; // 既存スタイル流用（必要ならPWA用に）

import { db } from '../../firebase/firebase';
import { cancelReservation } from '../../firebase/callFunctions';
import ReservedCard from '../../shared/components/ReservedCard';
import ClosedCard from '../../shared/components/ClosedCard';
import LoadingCard from '../../shared/components/LoadingCard';
import usePublicQueue from '../../shared/hooks/usePublicQueue';
import FooterPwa from '../components/FooterPwa';

// Firestore 側のユーザー識別フィールド名（必要なら 'uid' に変更）
const PWA_USER_ID_FIELD = 'pwaId';

export default function BookMyPwa() {
  // 1) PWA 認証
  const { uid, error } = usePwaAuth();
  const myIdResolved = (typeof uid === 'string') || !!error;

  // 2) ランタイム（待ち人数/開閉/自分の前人数）
  const queue = usePublicQueue({
    userId: (typeof uid === 'string') ? uid : null,
    userIdField: PWA_USER_ID_FIELD,
    enabled: true,
  });

  // 3) 自分の予約スナップショット
  const [resv, setResv] = useState(null);       // { no, slotId, at, reservedType, by }
  const [busyCancel, setBusyCancel] = useState(false);
  const [resvReady, setResvReady] = useState(false);

  useEffect(() => {
    setResv(null);
    setResvReady(false);

    if (!uid) return;

    const q = query(
      collection(db, 'reservation_slots'),
      where(PWA_USER_ID_FIELD, '==', uid),
      where('isReserved', '==', true),
      where('canceled', '==', false),
      where('isReceptionAccepted', '==', false),
      where('canceledPwaAt', '==', null),
    );

    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        const fromCache = snap.metadata.fromCache === true;

        if (snap.empty) {
          if (fromCache) return;   // サーバ応答待ち
          setResv(null);
          setResvReady(true);
          return;
        }

        // 同一ユーザーが複数保持していたら最小番号を採用
        let best = null;
        snap.forEach((docSnap) => {
          const d = docSnap.data();
          if (!best || (d.reservationNumber ?? 1e9) < (best.reservationNumber ?? 1e9)) {
            best = { id: docSnap.id, ...d };
          }
        });

        if (!best) {
          setResv(null);
          setResvReady(true);
          return;
        }

        const atMs = best.reservedAt?.toMillis ? best.reservedAt.toMillis() : Date.now();
        setResv({
          no: best.reservationNumber,
          slotId: best.id,
          by: uid,
          reservedType: best.reservedType || 'PWA',
          at: atMs,
        });
        setResvReady(true);
      },
      (err) => {
        console.error('reservation_slots watch error', err);
        setResv(null);
        setResvReady(true);
      }
    );

    return () => unsub();
  }, [uid]);

  // 4) 取消（Functions 側で canceledPwaAt を適切に設定）
  const handleCancel = async () => {
    if (!resv) return;
    setBusyCancel(true);
    try {
      await cancelReservation({
        slotId: resv.slotId,
        reservationNumber: resv.no,
        canceledBy: 'PWA',
      });
      setResv(null);
    } catch (e) {
      alert(e?.message || '予約の取消に失敗しました。');
    } finally {
      setBusyCancel(false);
    }
  };

  // 5) 画面分岐
  const authReady = uid !== undefined;

  if (error) {
    return (
      <div className="line-container">
        <div className="line-card">
          <h1 className="line-title">認証エラー</h1>
          <p className="line-desc">
            端末認証に失敗しました。ページを再読み込みしてください。改善しない場合は管理者へご連絡ください。
          </p>
        </div>
      </div>
    );
  }

  if (
    !authReady ||
    !queue.runtimeReady ||
    (authReady && !uid) ||
    ((typeof uid === 'string') && !resvReady)
  ) {
    return (
      <div className="line-container">
        <LoadingCard />
      </div>
    );
  }

  if (resv) {
    return (
      <div className="line-container">
        <ReservedCard
          resv={resv}
          myAheadCount={queue.myAheadCount}
          displayName={null}
          onCancel={handleCancel}
          cancelBusy={busyCancel}
          showLinks={false}
        />
        <FooterPwa />
      </div>
    );
  }

  if (!queue.isOnlineOpen) {
    return (
      <div className="line-container">
        <ClosedCard />
        <FooterPwa />
      </div>
    );
  }

  return (
    <div className="line-container">
      <div className="line-card line-card--stack">
        <h2 className="line-title">現在のご予約はありません。</h2>
        <p className="line-desc line-desc--muted">
          オンライン受付時間内です。受付をご希望の方は受付番号予約メニューからお進みください。
        </p>
        <div className="line-wait" style={{ marginTop: 12 }}>
          <div className="line-wait-caption">現在の待ち人数</div>
          <div className="line-wait-count">
            {queue.waitCount == null ? '—' : `${queue.waitCount}人`}
          </div>
        </div>
      </div>


      {/* デバッグ補助（任意） */}
      {/* <div className="line-muted" style={{marginTop:12}}>
        uid: {uid} ／ inhouseOn: {String(queue.inhouseOn)} ／ isOnlineOpen: {String(queue.isOnlineOpen)}
      </div> */}
      
      <FooterPwa />
    </div>
  );
}